import { useState } from 'react';
import Header from '../components/Header.jsx';
import AlphaPad from '../components/AlphaPad.jsx';
import { api } from '../lib/api.js';
import { setActiveSession } from '../lib/storage.js';
import { roundSignInHour } from '../lib/datetime.js';
import { IS_V2 } from '../lib/variant.js';

// CathayIDs are alphabetic (e.g. CRWPKW). Typical length is 6–7 letters
// but the format spec allows anything from 4 up to 10.
const MIN_LEN = 4;
const MAX_LEN = 10;
const VALID_RE = /^[A-Z]+$/;

// Lightweight uuid v4 generator without an extra dep — used inline.
function genUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Tiny fallback for older Safari versions.
  const a = new Uint8Array(16);
  crypto.getRandomValues(a);
  a[6] = (a[6] & 0x0f) | 0x40;
  a[8] = (a[8] & 0x3f) | 0x80;
  const h = [...a].map((b) => b.toString(16).padStart(2, '0'));
  return `${h.slice(0, 4).join('')}-${h.slice(4, 6).join('')}-${h.slice(6, 8).join('')}-${h.slice(8, 10).join('')}-${h.slice(10).join('')}`;
}

export default function SignInScreen({ device, onSignedIn, onOverlap, onCancel, onNeedStaffCard }) {
  const [cathayId, setCathayId] = useState('');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const slotHour = roundSignInHour(new Date());

  const submit = async () => {
    const id = cathayId.trim().toUpperCase();
    if (id.length < MIN_LEN || id.length > MAX_LEN) {
      setErr(`CathayID must be ${MIN_LEN}–${MAX_LEN} letters (typically 6–7)`);
      return;
    }
    if (!VALID_RE.test(id)) {
      setErr('CathayID must contain letters A–Z only');
      return;
    }
    // v2 — hand off to the staff-card capture screen, which calls
    // signin + uploadStaffCard together once the photo is taken.
    if (IS_V2) {
      onNeedStaffCard(id);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const client_uuid = genUuid();
      const signin_at_utc = new Date().toISOString();
      const r = await api.signin({ client_uuid, cathay_id: id, signin_at_utc });
      setActiveSession(r.session);
      onSignedIn(r.session);
    } catch (e) {
      if (e.status === 409 && e.data?.code === 'overlap') {
        onOverlap({ cathayId: id, existing: e.data.existing });
      } else if (e.data?.code === 'clock_skew') {
        setErr('iPad clock is out of sync. Ask your admin to check the date/time.');
      } else {
        setErr(e.message || 'Sign-in failed');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-100">
      <Header deviceName={device?.name} />
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-6">
        <div className="w-full max-w-2xl space-y-6">
          <header className="text-center space-y-1">
            <h1 className="text-kiosk-lg font-semibold text-cathay-700">Enter your CathayID</h1>
            <p className="text-kiosk text-slate-500">
              Typically 6–7 letters (CRWXXX or FOPXXX, etc).
            </p>
            <p className="text-kiosk text-slate-500">
              Session will be recorded for slot starting:
            </p>
            <p className="font-mono text-kiosk-lg text-slate-800">
              {String(slotHour).padStart(2, '0')}:00
            </p>
          </header>

          <div className="bg-white rounded-2xl px-6 py-5 text-center font-mono tracking-[0.3em] text-kiosk-xl text-slate-900 min-h-[5rem] flex items-center justify-center border border-slate-200">
            {cathayId || <span className="text-slate-300">CathayID</span>}
          </div>

          {err && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl px-4 py-3 text-kiosk">
              {err}
            </div>
          )}

          <AlphaPad
            value={cathayId}
            maxLen={MAX_LEN}
            onKey={(k) => setCathayId((s) => s + k)}
            onBackspace={() => setCathayId((s) => s.slice(0, -1))}
            onClear={() => setCathayId('')}
          />

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 text-kiosk-lg font-semibold py-5 rounded-2xl"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={busy || cathayId.length < MIN_LEN}
              onClick={submit}
              className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-50 text-white text-kiosk-lg font-semibold py-5 rounded-2xl shadow"
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
